import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Ard, UslLine } from "@amzn/fuse-accounting-central-datastore-client";
import { BLANK_ARD } from "src/components/constants/appConstants";
import { ModalMode } from "src/context/jem-gen/types";
import {
  clearDraft as clearDraftStorage,
  getDraftKey,
  loadDraft,
  saveDraft,
} from "./ardDraftStorage";

/** Debounce delay (ms) between user edits and persisting the draft. */
const DRAFT_SAVE_DEBOUNCE_MS = 400;

/**
 * Compute the "baseline" ARD that the form starts from when the modal opens.
 * Drafts and "modified field" highlighting are both diffed against this.
 *
 * - CREATE     -> blank ARD
 * - EDIT       -> the underlying server-side ARD
 * - DUPLICATE  -> the source ARD with `id` cleared (same as initial form data)
 */
const computeBaseline = (
  ardData: Ard | null,
  modalMode: ModalMode,
): Ard | null => {
  if (modalMode === ModalMode.CREATE) {
    return { ...BLANK_ARD };
  }
  if (!ardData) return null;
  if (modalMode === ModalMode.DUPLICATE) {
    return { ...ardData, id: "" };
  }
  return { ...ardData };
};

export const useFormDataManagement = (
  ardData: Ard | null,
  modalMode: ModalMode,
) => {
  const [formData, setFormData] = useState<Ard | null>(null);
  const [baseline, setBaseline] = useState<Ard | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);

  // The localStorage key used for persistence depends on modalMode + ARD id.
  const draftKey = useMemo(
    () => getDraftKey(modalMode, ardData),
    [modalMode, ardData],
  );

  // Capture the latest ardData inside save callbacks without re-triggering
  // the debounced effect every render.
  const ardDataRef = useRef<Ard | null>(ardData);
  ardDataRef.current = ardData;

  // Every time the initialization effect sets formData, we increment this
  // counter. The auto-save effect checks the counter: if > 0 it decrements
  // and skips (because the formData change came from initialization, not
  // from a user edit). Only when the counter is 0 do we actually persist.
  const skipNextSaveRef = useRef(0);

  // Initialize formData + baseline whenever the modal opens with new context.
  useEffect(() => {
    const newBaseline = computeBaseline(ardData, modalMode);
    setBaseline(newBaseline);

    // Try to restore an existing draft (silent auto-restore; loadDraft
    // also evicts stale / outdated drafts as a side-effect).
    const draft = loadDraft(draftKey, ardData);

    // Tell the auto-save effect to skip the next formData change (the one
    // we're about to trigger with setFormData below).
    skipNextSaveRef.current++;

    if (draft) {
      setFormData(draft.formData);
      setLastSavedAt(draft.savedAt);
    } else if (modalMode === ModalMode.CREATE) {
      setFormData({ ...BLANK_ARD });
      setLastSavedAt(null);
    } else if (modalMode === ModalMode.DUPLICATE && ardData) {
      setFormData({ ...ardData, id: "" });
      setLastSavedAt(null);
    } else if (ardData) {
      setFormData({ ...ardData });
      setLastSavedAt(null);
    } else {
      setFormData(null);
      setLastSavedAt(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ardData, modalMode, draftKey]);

  // Debounced auto-save to localStorage on every formData change.
  useEffect(() => {
    // Skip saves triggered by the initialization effect.
    if (skipNextSaveRef.current > 0) {
      skipNextSaveRef.current--;
      return;
    }
    if (!formData) return;
    if (!draftKey) return;

    const timer = setTimeout(() => {
      const savedAt = saveDraft(draftKey, formData, ardDataRef.current);
      if (savedAt) {
        setLastSavedAt(savedAt);
      }
    }, DRAFT_SAVE_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [formData, draftKey]);

  const handleChange = (field: keyof Ard, value: string) => {
    if (formData) {
      setFormData({
        ...formData,
        [field]: value,
      });
    }
  };

  const handleUslLineChange = (
    useCaseIndex: number,
    lineIndex: number,
    field: keyof UslLine,
    value: string,
  ) => {
    if (formData && formData.usecases) {
      const updatedUsecases = [...formData.usecases];
      const useCase = updatedUsecases[useCaseIndex];

      if (useCase && useCase.uslLines) {
        const updatedLines = [...useCase.uslLines];
        updatedLines[lineIndex] = {
          ...updatedLines[lineIndex],
          [field]: value,
        };

        updatedUsecases[useCaseIndex] = {
          ...useCase,
          uslLines: updatedLines,
        };

        setFormData({
          ...formData,
          usecases: updatedUsecases,
        });
      }
    }
  };

  /**
   * Remove the persisted draft from localStorage. Should be called after a
   * successful submit, or when the user explicitly discards their draft.
   */
  const clearDraft = useCallback(() => {
    clearDraftStorage(draftKey);
    setLastSavedAt(null);
  }, [draftKey]);

  /**
   * Clear the draft and reset formData to baseline atomically, suppressing
   * the debounced auto-save so the cleared draft isn't immediately re-persisted.
   */
  const resetToBaseline = useCallback(() => {
    if (!baseline) return;
    clearDraftStorage(draftKey);
    skipNextSaveRef.current++;
    setFormData({ ...baseline });
    setLastSavedAt(null);
  }, [baseline, draftKey]);

  return {
    formData,
    setFormData,
    handleChange,
    handleUslLineChange,
    /** Original (unmodified) ARD used as a diff baseline; `null` when not yet ready. */
    baseline,
    /** ISO timestamp when the draft was last persisted to localStorage; `null` if never. */
    lastSavedAt,
    /** Remove the persisted draft for the current context. */
    clearDraft,
    /** Clear draft and reset form to baseline without triggering auto-save. */
    resetToBaseline,
  };
};
